import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  MOVEWARE_JOB_QUERY,
  PROJECT_INTEGRATION_RECORDS_QUERY,
} from "src/constants/graphql/queries";
import { formatDate } from "./movewareActivityOptions";

const present = value => value !== undefined && value !== null && String(value).trim() !== "";

const text = (...candidates) => candidates.find(present) || "";

const same = (left, right) =>
  present(left) && present(right) && String(left).trim().toLowerCase() === String(right).trim().toLowerCase();

const roleName = role =>
  text(role?.entity?.name, [role?.entity?.firstName, role?.entity?.lastName].filter(Boolean).join(" "), role?.lastName);

const roleId = role => text(role?.entity?.id, role?.entity?.number, role?.parentValue);

// MoveWare notes arrive as HTML fragments. Parse them with the browser's
// inert DOMParser (nothing executes) and keep only the text, which is then
// rendered as plain React text, never as markup.
const stripHtml = value => {
  const withBreaks = String(value || "").replace(/<br\s*\/?>/gi, "\n");
  const text = new DOMParser().parseFromString(withBreaks, "text/html").body.textContent || "";

  return text
    .replace(/\r/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatAddress = address => {
  if (!address || typeof address !== "object") return "";

  const street = [address.line1, address.line2].map(part => String(part || "").trim()).filter(Boolean).join(", ");
  const locality = [address.city, address.state, address.postcode]
    .map(part => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

  return text(street && locality ? `${street}, ${locality}` : street || locality || address.fullAddress);
};

const addressContact = (...addresses) =>
  addresses.map(address => text(address?.contact, address?.fullName, address?.mobile, address?.phone)).find(Boolean) ||
  "";

const storageAccountLabel = account =>
  text(account?.description, account?.name, account?.number, account?.id) &&
  [
    text(account?.description, account?.name),
    text(account?.number, account?.id) && !same(account?.description, account?.number)
      ? `ID ${text(account?.number, account?.id)}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

function DetailRow({ children }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2 }}>{children}</Box>
  );
}

function Detail({ label, value, clamp }) {
  if (!present(value)) return null;

  return (
    <Box sx={{ mb: 0.75, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          whiteSpace: "pre-wrap",
          ...(clamp
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const warehouseNotes = (activities, jobNotes) => {
  const fromJob = [
    ["Crew notes", jobNotes?.crew],
    ["Internal notes", jobNotes?.internal],
    ["Shipping notes", jobNotes?.shipping],
  ]
    .map(([label, value]) => ({ label, value: stripHtml(value) }))
    .filter(item => present(item.value));

  const fromActivities = (activities || [])
    .map(activity => {
      const comment = stripHtml(activity.comment);
      const notes = stripHtml(activity.notes);
      const diary = text(activity.diaries, activity.activityType);
      const warehouseRelated =
        /[WOE]/i.test(diary) ||
        ["RecDock", "RecDockFFE", "RelDock", "LoadandReturn", "WarehouseWork"].includes(activity.type);

      if (!warehouseRelated || !(comment || notes)) return null;

      return {
        id: activity.id,
        title: text(activity.description, activity.type),
        date: formatDate(activity.date),
        body: [comment, same(notes, comment) ? "" : notes].filter(Boolean).join("\n\n"),
      };
    })
    .filter(Boolean);

  return { fromJob, fromActivities };
};

export default function MovewareJobConfirmDetails({ jobId, option, projectId, activityType, onStatus }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { data, loading, error } = useQuery(MOVEWARE_JOB_QUERY, {
    variables: { id: String(jobId) },
    fetchPolicy: "network-only",
  });
  const { data: projectData } = useQuery(PROJECT_INTEGRATION_RECORDS_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  useEffect(() => {
    onStatus?.({ loading, error: error || null });
  }, [error, loading, onStatus]);

  const releasing = (activityType || option?.type) === "RelDock";
  const job = data?.movewareJob?.job || {};
  const activities = data?.movewareJob?.activities || [];
  const storageAccounts = data?.movewareJob?.storageAccounts || [];
  const roles = job.roles || {};
  const notes = job.notes || {};
  const addresses = job.addresses || {};
  const dates = job.activityDates || {};
  const customerRole = roles.customer || roles.corporateAccount || roles.billTo;
  const projectRecord = projectData?.project?.externalRecords?.find(
    record => record.recordType === "storage_account"
  );
  const projectCustomerId = text(projectRecord?.data?.customer_id, projectRecord?.data?.customer_number);

  const jobNumber = text(job.number, option?.jobNumber, option?.jobId);
  const customerName = text(roleName(customerRole), job.name, job.fullname);
  const customerId = roleId(customerRole);
  const customerMismatch =
    present(customerId) && present(projectCustomerId) && String(customerId) !== String(projectCustomerId);
  const origin = formatAddress(addresses.origin) || formatAddress(addresses.Load);
  const destination = formatAddress(addresses.destination) || formatAddress(addresses.Delivery);
  const contact = text(
    addressContact(
      releasing ? addresses.Delivery : addresses.Load,
      addresses.Load,
      addresses.Delivery,
      addresses.origin,
      addresses.destination
    ),
    roles.originClient?.phone,
    roles.destinationClient?.phone
  );
  const branch = text(roles.branch?.entity?.name, roles.branch?.parentValue, job.branchCode);
  const branchCode = text(roles.branch?.entity?.number, roles.branch?.parentValue, job.branchCode);
  const branchLabel =
    present(branch) && present(branchCode) && !same(branch, branchCode)
      ? `${branch} (${branchCode})`
      : text(branch, branchCode);
  const storageLabels = storageAccounts.map(storageAccountLabel).filter(Boolean);
  const { fromJob, fromActivities } = warehouseNotes(activities, notes);
  const primaryNote = fromJob[0];
  const extraNotes = fromJob.slice(1);
  const dockDate = text(option?.date, formatDate(releasing ? dates.delivery?.date : dates.uplift?.date));
  const otherAddress = releasing ? origin : destination;
  const extraDetails = [
    !customerMismatch && customerId,
    contact,
    otherAddress && !same(otherAddress, releasing ? destination : origin),
    formatDate(dates.uplift?.date),
    formatDate(dates.delivery?.date),
    branchLabel,
    roleName(roles.coordinator || roles.moveManager),
    text(job.type),
    storageLabels.length,
    extraNotes.length,
    fromActivities.length,
  ].some(Boolean);

  if (loading) {
    return (
      <Box>
        <DetailRow>
          <Detail label="Job" value={text(option?.jobNumber, option?.jobId)} />
        </DetailRow>
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Box>
      <DetailRow>
        <Detail label="Job" value={jobNumber} />
        <Detail label="Customer" value={customerName} />
      </DetailRow>
      {customerMismatch && (
        <Alert severity="warning" sx={{ mb: 0.75 }}>
          This job belongs to a different customer
        </Alert>
      )}
      <Detail label={releasing ? "To" : "From"} value={releasing ? destination : origin} />
      <Detail label={releasing ? "Release" : "Receive"} value={dockDate} />
      {primaryNote && (
        <Detail label={primaryNote.label} value={primaryNote.value} clamp={!moreOpen} />
      )}
      {extraDetails && (
        <>
          <Button
            onClick={() => setMoreOpen(open => !open)}
            endIcon={
              <ExpandMoreIcon
                sx={{ transform: moreOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
              />
            }
            sx={{ textTransform: "none", px: 0, minHeight: 32, mb: moreOpen ? 1 : 0, fontSize: "1rem" }}
          >
            {moreOpen ? "Fewer details" : "More details"}
          </Button>
          <Collapse in={moreOpen} timeout="auto" unmountOnExit>
            <DetailRow>
              {!customerMismatch && <Detail label="Customer ID" value={customerId} />}
              <Detail label="Branch" value={branchLabel} />
            </DetailRow>
            <Detail label={releasing ? "From" : "To"} value={otherAddress} />
            <Detail label="Contact" value={contact} />
            <DetailRow>
              <Detail label="Uplift" value={formatDate(dates.uplift?.date)} />
              <Detail label="Delivery" value={formatDate(dates.delivery?.date)} />
            </DetailRow>
            <DetailRow>
              <Detail label="Coordinator" value={roleName(roles.coordinator || roles.moveManager)} />
              <Detail label="Job type" value={text(job.type)} />
            </DetailRow>
            {storageLabels.map(label => (
              <Detail key={label} label="Storage account" value={label} />
            ))}
            {extraNotes.map(item => (
              <Detail key={item.label} label={item.label} value={item.value} />
            ))}
            {fromActivities.map(item => (
              <Detail
                key={item.id}
                label={[item.title, item.date].filter(Boolean).join(" · ")}
                value={item.body}
              />
            ))}
          </Collapse>
        </>
      )}
    </Box>
  );
}
